/**
 * Central API Client for Feedback Survey App
 * Replaces all direct Supabase calls with backend API endpoints
 */

import { normalizeErrorMessage } from './errorMapper';
import { QuestionnaireSnapshotResponse } from '../types/entities';

// Fallback pinned to the current Cloud Run project (api-lab-0407,
// number 978240572015) so a build without VITE_API_BASE_URL doesn't
// silently ship a URL that points at a deprecated project. If both
// this and the env are unset, the whole submit path 500s — surfacing
// the misconfiguration loudly at build time rather than after SMS
// send.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://survey-app-api-978240572015.europe-central2.run.app';

// Debug flag - set to true in dev to enable submit debugging
const DEBUG_SUBMIT = import.meta.env.DEV || false;

// Session token storage (in-memory + sessionStorage)
let _sessionToken: string | null = null;

/**
 * Store session token in memory and sessionStorage
 */
export function setSessionToken(token: string | null): void {
  _sessionToken = token;
  if (token) {
    try {
      sessionStorage.setItem('survey_session_token', token);
    } catch (e) {
      console.warn('Failed to store session token in sessionStorage:', e);
    }
  } else {
    try {
      sessionStorage.removeItem('survey_session_token');
    } catch (e) {
      console.warn('Failed to remove session token from sessionStorage:', e);
    }
  }
}

/**
 * Retrieve session token from memory or sessionStorage
 */
export function getSessionToken(): string | null {
  if (_sessionToken) return _sessionToken;
  
  try {
    const stored = sessionStorage.getItem('survey_session_token');
    if (stored) {
      _sessionToken = stored;
      return stored;
    }
  } catch (e) {
    console.warn('Failed to retrieve session token from sessionStorage:', e);
  }
  
  return null;
}

/**
 * Clear session token
 */
export function clearSessionToken(): void {
  setSessionToken(null);
}

// Types
export interface TokenValidationResponse {
  hospital_id: string;
  hospital_name: string;
  link_id: string;
  is_used: boolean;
  expires_at: string;
  session_token: string;
  tip_servicii?: string | null;
  logo_url?: string | null;
}

export interface Doctor {
  id: string;
  full_name: string;
  specialization?: string;
}

export interface Question {
  id: string;
  position: number; // NEW: Display order
  type: string;
  label: string;
  options: any;
  is_required: boolean;
  is_active: boolean;
  allow_multiple_selection: boolean; // NEW: Enable multi-select for multiple_choice
  hospital_id: string;
  tip_servicii?: string;
  // Legacy fields for backward compatibility
  question_position?: number;
  order_index?: number;
  question_text?: string;
  question_type?: string;
}

export interface QuestionnaireResponse {
  questions: Question[];
  doctors: Doctor[];
  specializations: string[];
  hospital_name: string;
  hospital_id: string;
  tip_servicii?: string | null;
}

// New backend API types
export interface AnswerPayload {
  question_id: string;
  value: string;
}

export interface SubmitAnswersRequest {
  token: string;
  answers: AnswerPayload[];
  unsubscribe?: boolean;
}

export interface SubmitAnswersResponse {
  success: boolean;
  message: string;
}

// Legacy types (kept for backward compatibility)
export interface SubmitFeedbackAnswer {
  question_id: string;
  value: string;
  doctor_id?: string;
}

export interface SubmitFeedbackRequest {
  token: string;
  answers: SubmitFeedbackAnswer[];
}

export interface SubmitFeedbackResponse {
  success: boolean;
  message: string;
}

export interface RateLimitResponse {
  allowed: boolean;
  remaining?: number;
  reset_at?: string;
  message?: string;
}

// Custom error class for API errors
export class APIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorCode?: string,
    public metadata?: Record<string, any>
  ) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * Generic fetch wrapper with error handling
 */
async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const requestConfig = {
    ...options,
    mode: 'cors' as RequestMode,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, requestConfig);

    // Parse response body once
    let responseBody: any;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      responseBody = await response.json();
    } else {
      responseBody = await response.text();
    }

    // Handle non-2xx responses
    if (!response.ok) {
      const errorData = typeof responseBody === 'object' ? responseBody : {};
      
      // Get raw error message from backend
      const rawErrorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      
      // Normalize to user-friendly Romanian message
      const friendlyMessage = normalizeErrorMessage(rawErrorMessage);
      
      // Extract metadata from error response (e.g., hospital_id for used links)
      // Backend returns these fields in 409 Conflict responses for used links
      const metadata: Record<string, any> = {};
      if (errorData.hospital_id) metadata.hospital_id = errorData.hospital_id;
      if (errorData.hospital_name) metadata.hospital_name = errorData.hospital_name;
      if (errorData.tip_servicii) metadata.tip_servicii = errorData.tip_servicii;
      if (errorData.link_id) metadata.link_id = errorData.link_id;
      
      throw new APIError(
        friendlyMessage, // User-friendly message
        response.status,
        errorData.code || errorData.errorCode,
        Object.keys(metadata).length > 0 ? metadata : undefined
      );
    }

    // Return parsed response body (already parsed above)
    return responseBody as T;
  } catch (error) {
    // Network errors or JSON parsing errors
    if (error instanceof APIError) {
      throw error;
    }
    
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new APIError(
        normalizeErrorMessage('Network error'),
        0,
        'NETWORK_ERROR'
      );
    }
    
    // Normalize any other unexpected error
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new APIError(
      normalizeErrorMessage(errorMessage),
      0,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * 1. GET /api/feedback/validate-token?token=xxx
 * Validates the feedback token and returns session token
 * NOTE: This should be called EXACTLY ONCE per token value
 */
export async function validateToken(token: string): Promise<TokenValidationResponse> {
  if (!token || typeof token !== 'string' || token.trim() === '') {
    throw new APIError('Token invalid sau lipsă.', 400, 'INVALID_TOKEN');
  }

  try {
    const encodedToken = encodeURIComponent(token.trim());
    const endpoint = `/api/feedback/validate-token?token=${encodedToken}`;

    const response = await fetchAPI<TokenValidationResponse>(endpoint, {
      method: 'GET',
    });

    // Validate required fields
    if (!response.session_token || !response.hospital_id) {      throw new APIError(
        'Răspuns invalid de la server.',
        500,
        'INVALID_RESPONSE'
      );
    }

    // Check if link is already used
    if (response.is_used === true) {
      throw new APIError(
        'Acest link de feedback a fost deja utilizat.',
        403,
        'LINK_ALREADY_USED',
        {
          hospital_id: response.hospital_id,
          hospital_name: response.hospital_name,
          tip_servicii: response.tip_servicii
        }
      );
    }

    // Check if link is expired
    if (response.expires_at) {
      const expiresAt = new Date(response.expires_at);
      if (expiresAt < new Date()) {
        throw new APIError(
          'Acest link de feedback a expirat.',
          403,
          'LINK_EXPIRED',
          {
            hospital_id: response.hospital_id,
            hospital_name: response.hospital_name,
            tip_servicii: response.tip_servicii
          }
        );
      }
    }

    // Store session token automatically
    setSessionToken(response.session_token);

    return response;
  } catch (error) {
    if (error instanceof APIError) {
      // Map specific HTTP status codes to user-friendly messages
      if (error.statusCode === 404) {
        throw new APIError('Link invalid sau expirat.', 404, 'LINK_NOT_FOUND');
      }
      if (error.statusCode === 403) {
        throw error; // Already has user-friendly message
      }
      throw error;
    }
    throw new APIError(
      'Eroare la validarea link-ului. Vă rugăm să încercați din nou.',
      0,
      'VALIDATION_ERROR'
    );
  }
}

/**
 * 2. GET /api/feedback/questionnaire
 * Fetches complete questionnaire snapshot (hospital, link, questions, doctors, specializations)
 * This is the ONLY endpoint that should be called after validate-token
 */
export async function getQuestionnaire(sessionToken: string): Promise<QuestionnaireSnapshotResponse> {
  if (!sessionToken || typeof sessionToken !== 'string' || sessionToken.trim() === '') {
    throw new APIError('Token de sesiune invalid.', 401, 'INVALID_SESSION_TOKEN');
  }

  try {
    const response = await fetchAPI<QuestionnaireSnapshotResponse>('/api/feedback/questionnaire', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${sessionToken.trim()}`,
      },
    });

    // Validate response structure
    if (!response.hospital || !response.link || !response.questions) {
      throw new APIError(
        'Răspuns invalid de la server: lipsesc date esențiale.',
        500,
        'INVALID_RESPONSE'
      );
    }

    if (!Array.isArray(response.questions)) {
      throw new APIError(
        'Răspuns invalid de la server: lipsesc întrebările.',
        500,
        'INVALID_RESPONSE'
      );
    }

    // Ensure optional arrays exist (even if empty)
    response.doctors = response.doctors || [];
    response.specializations = response.specializations || [];

    return response;
  } catch (error) {
    // fetchAPI already normalized the error message
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      normalizeErrorMessage('Failed to load questionnaire'),
      0,
      'QUESTIONNAIRE_LOAD_ERROR'
    );
  }
}

/**
 * 3. POST /api/feedback/submit
 * Submits feedback answers
 */
export async function submitFeedback(
  sessionToken: string,
  token: string,
  answers: SubmitFeedbackAnswer[]
): Promise<SubmitFeedbackResponse> {
  if (!sessionToken || typeof sessionToken !== 'string' || sessionToken.trim() === '') {
    throw new APIError('Token de sesiune invalid.', 401, 'INVALID_SESSION_TOKEN');
  }

  if (!token || typeof token !== 'string' || token.trim() === '') {
    throw new APIError('Token invalid.', 400, 'INVALID_TOKEN');
  }

  if (!Array.isArray(answers) || answers.length === 0) {
    throw new APIError('Nu există răspunsuri de trimis.', 400, 'NO_ANSWERS');
  }

  // Validate each answer
  for (const answer of answers) {
    if (!answer.question_id || typeof answer.question_id !== 'string') {
      throw new APIError('Răspuns invalid: lipsește ID-ul întrebării.', 400, 'INVALID_ANSWER');
    }
    if (answer.value === undefined || answer.value === null) {
      throw new APIError('Răspuns invalid: lipsește valoarea.', 400, 'INVALID_ANSWER');
    }
  }

  try {
    const response = await fetchAPI<SubmitFeedbackResponse>('/api/feedback/submit', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionToken.trim()}`,
      },
      body: JSON.stringify({
        token: token.trim(),
        answers,
      }),
    });

    return response;
  } catch (error) {
    if (error instanceof APIError) {
      if (error.statusCode === 401) {
        throw new APIError(
          'Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.',
          401,
          'SESSION_EXPIRED'
        );
      }
      if (error.statusCode === 403) {
        throw new APIError(
          'Acest link a fost deja utilizat.',
          403,
          'LINK_ALREADY_USED'
        );
      }
      if (error.statusCode === 429) {
        throw new APIError(
          'Prea multe încercări. Vă rugăm să încercați din nou mai târziu.',
          429,
          'RATE_LIMIT_EXCEEDED'
        );
      }
      throw error;
    }
    throw new APIError(
      'Eroare la trimiterea răspunsurilor. Vă rugăm să încercați din nou.',
      0,
      'SUBMISSION_ERROR'
    );
  }
}

/**
 * 3B. POST /api/feedback/submit - Simplified version
 * Submits feedback answers with session token in Authorization header and link token in body
 * @param sessionToken - Session token from /validate-token response (for Authorization header)
 * @param token - The feedback link token from URL (for request body)
 * @param answers - Array of answer payloads (question_id + value only)
 */
export async function submitAnswers(
  sessionToken: string,
  token: string,
  answers: AnswerPayload[],
  unsubscribe: boolean = false
): Promise<SubmitAnswersResponse> {
  // Validate session token
  if (!sessionToken || typeof sessionToken !== 'string' || sessionToken.trim() === '') {
    throw new APIError(
      'Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.',
      401,
      'SESSION_EXPIRED'
    );
  }

  // Validate link token
  if (!token || typeof token !== 'string' || token.trim() === '') {
    throw new APIError(
      'Token lipsește.',
      400,
      'MISSING_TOKEN'
    );
  }

  if (!Array.isArray(answers) || answers.length === 0) {
    throw new APIError('Nu există răspunsuri de trimis.', 400, 'NO_ANSWERS');
  }

  // Validate each answer
  for (const answer of answers) {
    if (!answer.question_id || typeof answer.question_id !== 'string') {
      throw new APIError('Răspuns invalid: lipsește ID-ul întrebării.', 400, 'INVALID_ANSWER');
    }
    if (answer.value === undefined || answer.value === null) {
      throw new APIError('Răspuns invalid: lipsește valoarea.', 400, 'INVALID_ANSWER');
    }
  }

  try {
    // Build request body with link token
    const requestBody: SubmitAnswersRequest = {
      token: token.trim(),
      answers,
      unsubscribe,
    };

    // CRITICAL: Send session token in Authorization header
    const response = await fetchAPI<SubmitAnswersResponse>('/api/feedback/submit', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionToken.trim()}`,
      },
      body: JSON.stringify(requestBody),
    });

    return response;
  } catch (error) {
    // Map specific error codes to user-friendly messages
    if (error instanceof APIError) {
      // Map backend error codes to Romanian messages
      if (error.statusCode === 400 || error.errorCode === 'VALIDATION_ERROR') {
        throw new APIError(
          'Date invalide. Reîncearcă.',
          400,
          'VALIDATION_ERROR'
        );
      }
      if (error.statusCode === 401) {
        throw new APIError(
          'Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.',
          401,
          'SESSION_EXPIRED'
        );
      }
      if (error.statusCode === 409 || error.errorCode === 'LINK_ALREADY_USED') {
        throw new APIError(
          'Chestionarul a fost deja completat.',
          409,
          'LINK_ALREADY_USED'
        );
      }
      if (error.statusCode === 410 || error.errorCode === 'LINK_EXPIRED') {
        throw new APIError(
          'Link expirat.',
          410,
          'LINK_EXPIRED'
        );
      }
      if (error.statusCode === 404 || error.errorCode === 'LINK_NOT_FOUND') {
        throw new APIError(
          'Link invalid.',
          404,
          'LINK_NOT_FOUND'
        );
      }
      if (error.statusCode === 500 || error.errorCode === 'DATABASE_ERROR') {
        throw new APIError(
          'Eroare de server. Vă rugăm să încercați din nou.',
          500,
          'DATABASE_ERROR'
        );
      }
      // Re-throw other API errors as-is
      throw error;
    }
    // For any unexpected non-API errors
    throw new APIError(
      'Eroare la trimiterea răspunsurilor. Vă rugăm să încercați din nou.',
      0,
      'SUBMISSION_ERROR'
    );
  }
}

/**
 * 4. GET /api/feedback/rate-limit?token=<token>
 * Checks rate limit status for a token
 */
export async function checkRateLimit(token: string): Promise<RateLimitResponse> {
  if (!token || typeof token !== 'string' || token.trim() === '') {
    throw new APIError('Token invalid.', 400, 'INVALID_TOKEN');
  }

  try {
    const response = await fetchAPI<RateLimitResponse>(
      `/api/feedback/rate-limit?token=${encodeURIComponent(token.trim())}`,
      {
        method: 'GET',
      }
    );

    return response;
  } catch (error) {
    // fetchAPI already normalized the error message
    if (error instanceof APIError) {
      if (error.statusCode === 429) {
        return {
          allowed: false,
          message: error.message, // Already normalized
        };
      }
      throw error;
    }
    throw new APIError(
      normalizeErrorMessage('Rate limit check error'),
      0,
      'RATE_LIMIT_CHECK_ERROR'
    );
  }
}

/**
 * 🔍 DEBUG: Intercept all fetch calls in dev mode (install once)
 * This will catch any direct Supabase or unexpected API calls
 */
if (DEBUG_SUBMIT && typeof window !== 'undefined') {
  // Global guard to prevent multiple installations
  if (!(window as any).__SURVEY_FETCH_WRAPPED__) {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      // CRITICAL: Call originalFetch, NOT window.fetch (avoid recursion)
      return originalFetch.apply(window, args);
    };
    
    // Mark as wrapped
    (window as any).__SURVEY_FETCH_WRAPPED__ = true;
  }
}

/**
 * Export API base URL and debug status for UI display
 */
export const getApiDebugInfo = () => ({
  apiBaseUrl: API_BASE_URL,
  envVarValue: import.meta.env.VITE_API_BASE_URL,
  debugEnabled: DEBUG_SUBMIT,
  nodeEnv: import.meta.env.MODE
});
